import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import planoService from '../services/planoTrabalhoService';
import metaService from '../services/metaService';
import itemService from '../services/itemService';
import indicadorPlanoService from '../services/indicadorPlanoService';
import institutionService, { type InstitutionDTO } from '../services/institutionService';
import dirigenteService from '../services/dirigenteService';
import emendaService, { type EmendaDTO } from '../services/emendaService';
import type { PlanoTrabalho } from '../types/planoTrabalho.types';
import type { Meta } from '../services/metaService';
import type { Item } from '../services/itemService';
import type { IndicadorPlano } from '../services/indicadorPlanoService';
import type { Dirigente } from '../types/dirigente.types';
import { formatCep, formatCnpj, formatCurrency, formatPhoneBR, onlyDigits } from '../utils/formatters';

type CronogramaRow = {
  metaTitulo?: string;
  meta?: string;
  itemTitulo?: string;
  item?: string;
  unidade?: string;
  quantidade?: number;
  periodoInicio?: string;
  periodoFim?: string;
};

type PrevisaoData = {
  totalPlano: number;
  anos: Array<{ year: string; receitas: number; despesas: number }>;
};

type AplicacaoItem = {
  id?: string;
  itemId?: string;
  titulo?: string;
  itemTitulo?: string;
  descricao?: string;
  unidade?: string;
  quantidade?: number;
  valorUnitario?: number;
  valorTotal?: number;
  valor?: number;
};

type AplicacaoOrcamento = {
  id?: string;
  valor?: number;
  nomeOriginal?: string;
  viewUrl?: string;
};

type AplicacaoGrupo = {
  metaId?: string;
  metaTitulo?: string;
  totalMeta?: number;
  items?: AplicacaoItem[];
  orcamentos?: AplicacaoOrcamento[];
};

type DesembolsoMes = {
  mes?: string;
  month?: string;
  valor?: number;
  total?: number;
  totalMes?: number;
};

type DesembolsoData = {
  meses?: DesembolsoMes[];
  months?: DesembolsoMes[];
  totalPlano?: number;
};

const monthFmt = new Intl.DateTimeFormat('pt-BR', { month: '2-digit', year: 'numeric' });
const dateFmt = new Intl.DateTimeFormat('pt-BR');

const formatMonthYear = (value?: string | null) => {
  if (!value) return 'Não informado';
  // Extract YYYY-MM from any ISO format (e.g. "2026-05" or "2026-05-01T00:00:00Z")
  // and build a local-time Date to avoid UTC offset shifting the displayed month.
  const ymMatch = String(value).match(/^(\d{4})-(\d{2})/);
  if (!ymMatch) return String(value);
  const date = new Date(`${ymMatch[1]}-${ymMatch[2]}-01T00:00:00`);
  return Number.isNaN(date.getTime()) ? String(value) : monthFmt.format(date);
};

const formatDate = (value?: string | null) => {
  if (!value) return 'Não informado';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateFmt.format(date);
};

const formatCpf = (value?: string | null) => {
  const digits = onlyDigits(String(value || '')).slice(0, 11);
  if (!digits) return 'Não informado';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

const formatText = (value?: string | null) => {
  const text = String(value || '').trim();
  return text || 'Não informado';
};

const normalizeMonthLabel = (value?: string | null) => {
  const raw = String(value || '').trim();
  if (!raw) return 'Não informado';
  if (/^\d{4}-\d{2}/.test(raw)) {
    const date = new Date(`${raw.slice(0, 7)}-01`);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString('pt-BR', { month: 'long' }).toUpperCase();
    }
  }
  return raw.toUpperCase();
};

const Field: React.FC<{ label: string; value?: React.ReactNode; className?: string }> = ({ label, value, className = '' }) => (
  <div className={`rounded-md border border-slate-200 px-2.5 py-2 print-keep-together ${className}`}>
    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</div>
    <div className="mt-0.5 text-[13px] leading-5 text-slate-900">{value ?? 'Não informado'}</div>
  </div>
);

const SectionTitle: React.FC<{ number: string; title: string }> = ({ number, title }) => (
  <div className="mb-3 mt-8 border-b border-slate-300 pb-2">
    <h2 className="text-base font-bold uppercase tracking-[0.12em] text-slate-900">{number} - {title}</h2>
  </div>
);

type TableCellProps = React.ThHTMLAttributes<HTMLTableCellElement> &
  React.TdHTMLAttributes<HTMLTableCellElement> & {
    head?: boolean;
    align?: 'left' | 'center' | 'right';
  };

const TableCell: React.FC<TableCellProps> = ({ children, head, align = 'left', className = '', ...props }) => {
  const Tag = head ? 'th' : 'td';
  const alignment = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <Tag
      className={`border border-slate-300 px-2 py-2 align-top text-xs whitespace-normal break-words ${alignment} ${head ? 'bg-slate-100 font-semibold uppercase tracking-[0.08em] text-slate-700' : 'text-slate-800'} ${className}`}
      {...props}
    >
      {children}
    </Tag>
  );
};

const PlanoTrabalhoPrintPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plan, setPlan] = useState<PlanoTrabalho | null>(null);
  const [institution, setInstitution] = useState<InstitutionDTO | null>(null);
  const [dirigente, setDirigente] = useState<Dirigente | null>(null);
  const [emenda, setEmenda] = useState<EmendaDTO | null>(null);
  const [metas, setMetas] = useState<Meta[]>([]);
  const [itemsByMeta, setItemsByMeta] = useState<Record<string, Item[]>>({});
  const [indicadores, setIndicadores] = useState<IndicadorPlano[]>([]);
  const [previsao, setPrevisao] = useState<PrevisaoData | null>(null);
  const [aplicacao, setAplicacao] = useState<AplicacaoGrupo[]>([]);
  const [desembolso, setDesembolso] = useState<DesembolsoData | null>(null);
  const [retificacoes, setRetificacoes] = useState<{ id: string; dataHora?: string; usuario?: string }[]>([]);

  useEffect(() => {
    if (!id) {
      setError('Plano não informado.');
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const planResponse = await planoService.getFull(id);
        const plano = planResponse.data as PlanoTrabalho;
        setPlan(plano);

        const metasData = await metaService.listByPlano(id).catch(() => []);
        setMetas(metasData);

        const itemEntries = await Promise.all(
          metasData
            .filter(meta => Boolean(meta.id))
            .map(async (meta) => [meta.id as string, await itemService.listByMeta(meta.id as string).catch(() => [])] as const)
        );
        setItemsByMeta(Object.fromEntries(itemEntries));

        const [
          institutionData,
          emendaData,
          indicadoresData,
          previsaoData,
          aplicacaoData,
          desembolsoData,
        ] = await Promise.all([
          plano.instituicaoId ? institutionService.getByInstitutionId(plano.instituicaoId).catch(() => null) : Promise.resolve(null),
          plano.emendaId ? emendaService.getByIdWithDetails(plano.emendaId).catch(() => null) : Promise.resolve(null),
          indicadorPlanoService.listByPlano(id).catch(() => []),
          planoService.getPrevisao(id).catch(() => null),
          planoService.getAplicacao(id).catch(() => []),
          planoService.getDesembolso(id).catch(() => null),
        ]);

        let dirigenteData: Dirigente | null = null;
        if (plano.instituicaoId) {
          const dirigentesAtivos = await dirigenteService.listar(plano.instituicaoId, true).catch(() => []);
          dirigenteData =
            dirigentesAtivos.find((item) => item.cargo === 'Presidente')
            || dirigentesAtivos.find((item) => item.cargo === 'Vice-Presidente')
            || null;
          if (!dirigenteData && plano.dirigenteId) {
            dirigenteData = await dirigenteService.buscarPorId(plano.dirigenteId).catch(() => null);
          }
          if (!dirigenteData) {
            dirigenteData = dirigentesAtivos[0] || null;
          }
        } else if (plano.dirigenteId) {
          dirigenteData = await dirigenteService.buscarPorId(plano.dirigenteId).catch(() => null);
        }

        setInstitution(institutionData as InstitutionDTO | null);
        setDirigente(dirigenteData as Dirigente | null);
        setEmenda(emendaData as EmendaDTO | null);
        setIndicadores(indicadoresData as IndicadorPlano[]);
        setPrevisao(previsaoData as PrevisaoData | null);
        setAplicacao(aplicacaoData as AplicacaoGrupo[]);
        setDesembolso(desembolsoData as DesembolsoData | null);

        const historico = await planoService.getHistorico(id).catch(() => []);
        const retifs = historico
          .filter((h: any) => h.acao === 'SOLICITAR_RETIFICACAO')
          .map((h: any) => ({ id: h.id as string, dataHora: h.dataHora as string | undefined, usuario: h.usuario as string | undefined }));
        setRetificacoes(retifs);
      } catch (err: any) {
        setError(err?.response?.data?.error || 'Não foi possível carregar o impresso do plano.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  useEffect(() => {
    if (!loading && !error && searchParams.get('autoprint') === '1') {
      const timeout = window.setTimeout(() => window.print(), 400);
      return () => window.clearTimeout(timeout);
    }
    return undefined;
  }, [loading, error, searchParams]);

  const projetoPeriodo = useMemo(() => {
    if (!plan?.periodoInicio && !plan?.periodoFim) return 'Não informado';
    return `${formatMonthYear(plan?.periodoInicio)} a ${formatMonthYear(plan?.periodoFim)}`;
  }, [plan?.periodoInicio, plan?.periodoFim]);

  const totalMetas = useMemo(() => {
    if (aplicacao.length > 0) {
      return aplicacao.reduce((sum, g) => sum + (g.totalMeta ?? 0), 0);
    }
    if (typeof previsao?.totalPlano === 'number') return previsao.totalPlano;
    if (typeof plan?.valor === 'number') return plan.valor;
    if (typeof emenda?.value === 'number') return emenda.value;
    return 0;
  }, [aplicacao, previsao?.totalPlano, plan?.valor, emenda?.value]);
  const valorRepasse = useMemo(() => {
    if (typeof emenda?.value === 'number' && emenda.value > 0) return emenda.value;
    if (typeof plan?.emendaValor === 'number' && plan.emendaValor > 0) return plan.emendaValor;
    if (typeof plan?.valor === 'number') return plan.valor;
    return totalMetas;
  }, [emenda?.value, plan?.emendaValor, plan?.valor, totalMetas]);
  const valorContrapartida = useMemo(() => {
    const diferenca = totalMetas - valorRepasse;
    return diferenca > 0 ? diferenca : 0;
  }, [totalMetas, valorRepasse]);
  const totalPlano = useMemo(() => {
    if (typeof plan?.valor === 'number') return plan.valor;
    if (typeof previsao?.totalPlano === 'number') return previsao.totalPlano;
    if (typeof emenda?.value === 'number') return emenda.value;
    return 0;
  }, [plan?.valor, previsao?.totalPlano, emenda?.value]);
  const valorTotalReceitaDespesa = useMemo(() => valorRepasse + valorContrapartida, [valorRepasse, valorContrapartida]);
  const totalDesembolsoReferencia = useMemo(() => {
    if (typeof emenda?.value === 'number') return emenda.value;
    if (typeof desembolso?.totalPlano === 'number') return desembolso.totalPlano;
    return totalPlano;
  }, [desembolso?.totalPlano, emenda?.value, totalPlano]);

  const desembolsoMeses = (desembolso?.meses || desembolso?.months || []) as DesembolsoMes[];
  const subtotalAplicacao = aplicacao.reduce((acc, grupo) => acc + Number(grupo.totalMeta || 0), 0);
  const isDraft = (plan?.status || 'RASCUNHO') === 'RASCUNHO';
  const cronogramaFisico = useMemo<CronogramaRow[]>(() => {
    const rows: CronogramaRow[] = [];
    metas.forEach((meta) => {
      const items = itemsByMeta[meta.id || ''] || [];
      if (items.length === 0) {
        rows.push({ metaTitulo: meta.titulo || 'Meta', itemTitulo: meta.descricao });
      } else {
        items.forEach((item) => {
          rows.push({
            metaTitulo: meta.titulo || 'Meta',
            itemTitulo: item.descricao,
            unidade: item.unidade,
            periodoInicio: item.periodoInicio,
            periodoFim: item.periodoFim || item.periodoInicio,
          });
        });
      }
    });
    return rows;
  }, [itemsByMeta, metas]);

  if (loading) {
    return <div className="min-h-screen bg-slate-50 px-4 py-10 text-center text-slate-600">Carregando impresso do plano de trabalho...</div>;
  }

  if (error || !plan) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-10">
        <div className="mx-auto max-w-2xl rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <p className="text-lg font-semibold text-red-700">Erro ao gerar impresso</p>
          <p className="mt-2 text-sm text-slate-600">{error || 'Plano não encontrado.'}</p>
          <button type="button" onClick={() => navigate(-1)} className="mt-5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white">
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      <style>{`
        @page { size: A4 portrait; margin: 14mm; }
        @media print {
          .print-hidden { display: none !important; }
          .print-sheet { box-shadow: none !important; margin: 0 !important; max-width: none !important; }
          .print-no-scroll { overflow: visible !important; }
          .print-table-fit { table-layout: fixed !important; width: 100% !important; }
          .print-watermark {
            position: fixed !important;
            inset: 0 !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            pointer-events: none !important;
            z-index: 9999 !important;
          }
          .print-watermark-text {
            transform: rotate(-28deg) !important;
            font-size: 96px !important;
            line-height: 1 !important;
            font-weight: 800 !important;
            letter-spacing: 0.4em !important;
            color: rgba(148, 163, 184, 0.2) !important;
            text-transform: uppercase !important;
            white-space: nowrap !important;
          }
          .print-keep-together {
            break-inside: avoid-page !important;
            page-break-inside: avoid !important;
          }
          .print-keep-with-next {
            break-after: avoid-page !important;
            page-break-after: avoid !important;
          }
          body { background: #fff !important; }
        }
      `}</style>

      <div className="print-hidden sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">Impresso do Plano de Trabalho</p>
            <p className="text-xs text-slate-500">Modelo baseado no anexo 2026</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => navigate(-1)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700">
              Voltar
            </button>
            <button type="button" onClick={() => window.print()} className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white">
              Imprimir
            </button>
          </div>
        </div>
      </div>

      <main className="print-sheet relative mx-auto my-6 max-w-5xl rounded-3xl bg-white px-6 py-8 shadow-xl print:my-0 print:rounded-none print:px-0 print:py-0">
        {isDraft && (
          <div className="print-watermark" aria-hidden="true">
            <div className="print-watermark-text">RASCUNHO</div>
          </div>
        )}
        <header className="border-b border-slate-300 pb-5">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">Anexo 1</p>
          <h1 className="mt-2 text-center text-2xl font-bold uppercase tracking-[0.16em] text-slate-900">Plano de Trabalho</h1>

          <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-4 print:grid-cols-4">
            <Field label="Nome do Projeto" value={formatText(plan.titulo)} className="sm:col-span-2 print:col-span-2" />
            <Field label="Período de Execução" value={projetoPeriodo} />
            <Field label="Emenda Nº" value={formatText(emenda?.officialCode || plan.emendaCodigo)} />
            <Field label="Vereador Autor da Emenda" value={formatText(emenda?.councilorName)} className="sm:col-span-2 print:col-span-2" />
          </div>

          <div className="mt-4 rounded-xl border border-slate-200 px-4 py-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Modalidade</div>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <div className={`rounded-lg border px-3 py-2 text-sm ${plan.modalidade === 'TERMO_COLABORACAO' ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 text-slate-700'}`}>Termo de Colaboração</div>
              <div className={`rounded-lg border px-3 py-2 text-sm ${plan.modalidade === 'TERMO_FOMENTO' ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 text-slate-700'}`}>Termo de Fomento</div>
            </div>
          </div>
        </header>

        <SectionTitle number="1" title="Dados Cadastrais" />
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 print:grid-cols-4">
          <Field label="Organização da Sociedade Civil" value={formatText(institution?.razaoSocial || institution?.nomeFantasia)} className="sm:col-span-2 print:col-span-2" />
          <Field label="CNPJ" value={institution?.cnpj ? formatCnpj(institution.cnpj) : 'Não informado'} />
          <Field label="DDD/Telefone" value={institution?.telefone ? formatPhoneBR(institution.telefone) : 'Não informado'} />
          <Field label="Endereço" value={formatText([institution?.logradouro, institution?.numero, institution?.bairro].filter(Boolean).join(', '))} className="sm:col-span-2 print:col-span-2" />
          <Field label="CEP" value={institution?.cep ? formatCep(institution.cep) : 'Não informado'} />
          <Field label="Cidade" value={formatText(institution?.cidade)} />
          <Field label="Estado" value={formatText(institution?.uf)} />
          <Field label="E-mail" value={formatText(institution?.emailInstitucional)} className="sm:col-span-2 print:col-span-2" />
          <Field label="Conta Corrente" value={formatText(plan.contaBancaria)} className="sm:col-span-2 print:col-span-2" />
          <Field label="Banco" value={formatText(plan.banco)} />
          <Field label="Agência" value={formatText(plan.agencia)} />
        </div>

        <SectionTitle number="2" title="Representante Legal" />
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 print:grid-cols-4">
          <Field label="Nome do Representante Legal da Entidade" value={formatText(dirigente?.nomeCompleto)} className="sm:col-span-2 print:col-span-2" />
          <Field label="CPF" value={formatCpf(dirigente?.cpf)} />
          <Field label="RG / Órgão" value={dirigente ? `${formatText(dirigente.rg)} / ${formatText(dirigente.orgaoExpedidor)}` : 'Não informado'} />
          <Field label="Cargo" value={formatText(dirigente?.cargo)} />
          <Field label="E-mail" value={formatText(dirigente?.email)} className="sm:col-span-2 print:col-span-2" />
          <Field label="Endereço" value={dirigente ? formatText([dirigente.logradouro, dirigente.numero, dirigente.bairro].filter(Boolean).join(', ')) : 'Não informado'} className="sm:col-span-2 print:col-span-2" />
          <Field label="CEP" value={dirigente?.cep ? formatCep(dirigente.cep) : 'Não informado'} />
          <Field label="Cidade/UF" value={dirigente ? `${formatText(dirigente.cidade)} / ${formatText(dirigente.uf)}` : 'Não informado'} />
        </div>

        <SectionTitle number="3" title="Descrição do Objeto" />
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 print:grid-cols-2">
          <Field
            label="Objeto da Parceria"
            value={<p className="whitespace-pre-wrap">{formatText(plan.objetoParceria)}</p>}
            className="sm:col-span-2 print:col-span-2"
          />
          <Field
            label="Descrição da Realidade"
            value={<p className="whitespace-pre-wrap">{formatText(plan.descricaoRealidade)}</p>}
            className="sm:col-span-2 print:col-span-2"
          />
          <Field
            label="Descrição das Metas e Indicadores"
            className="sm:col-span-2 print:col-span-2"
            value={
              <div className="space-y-2">
                {metas.length > 0 ? metas.map((meta, index) => (
                  <div key={meta.id || index} className="space-y-0.5">
                    <p className="font-semibold text-slate-900">{meta.titulo || `Meta ${index + 1}`}</p>
                    {meta.descricao && <p className="whitespace-pre-wrap text-slate-700">{meta.descricao}</p>}
                    {(itemsByMeta[meta.id || ''] || []).length > 0 && (
                      <ul className="mt-1 list-disc pl-5 text-slate-700">
                        {(itemsByMeta[meta.id || ''] || []).map((item, itemIndex) => (
                          <li key={item.id || itemIndex}>
                            <span className="font-medium">{item.descricao || `Etapa ${itemIndex + 1}`}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )) : <p>Não informado</p>}
                {indicadores.length > 0 && (
                  <div className="pt-2">
                    <p className="font-semibold text-slate-900 mb-1">Indicadores</p>
                    <table className="w-full border-collapse">
                      <thead>
                        <tr>
                          <TableCell head>Descrição</TableCell>
                          <TableCell head align="center">Unidade</TableCell>
                          <TableCell head align="center">Quantidade</TableCell>
                        </tr>
                      </thead>
                      <tbody>
                        {indicadores.map((indicador) => (
                          <tr key={indicador.id}>
                            <TableCell>{formatText(indicador.descricao)}</TableCell>
                            <TableCell align="center">{formatText(indicador.unidade)}</TableCell>
                            <TableCell align="center">{indicador.quantidade ?? 0}</TableCell>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            }
          />
          <Field
            label="Forma de Execução das Ações"
            value={<p className="whitespace-pre-wrap">{formatText(plan.formaExecucao)}</p>}
            className="sm:col-span-2 print:col-span-2"
          />
        </div>

        <SectionTitle number="4" title="Cronograma de Execução Físico" />
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <TableCell head>Descrição da Meta / Ação</TableCell>
                <TableCell head align="center">Início</TableCell>
                <TableCell head align="center">Fim</TableCell>
              </tr>
            </thead>
            <tbody>
              {cronogramaFisico.map((row, index) => (
                <tr key={`${row.metaTitulo || row.meta || 'meta'}-${index}`}>
                  <TableCell>
                    <div className="font-semibold">{formatText(row.metaTitulo || row.meta)}</div>
                    {(row.itemTitulo || row.item) ? <div>{formatText(row.itemTitulo || row.item)}</div> : null}
                  </TableCell>
                  <TableCell align="center">{row.periodoInicio ? formatDate(row.periodoInicio) : '—'}</TableCell>
                  <TableCell align="center">{row.periodoFim ? formatDate(row.periodoFim) : '—'}</TableCell>
                </tr>
              ))}
              {cronogramaFisico.length === 0 && (
                <tr>
                  <TableCell colSpan={3}>Nenhum cronograma cadastrado.</TableCell>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <SectionTitle number="5" title="Previsão de Receitas e Despesas" />
        <div className="overflow-x-auto">
          <table className="print-table-fit w-full border-collapse">
            <thead>
              <tr>
                <TableCell head align="center" className="w-[14%]"></TableCell>
                <TableCell head className="w-[28%]">Valor de Repasse</TableCell>
                <TableCell head className="w-[28%]">Contrapartida</TableCell>
                <TableCell head className="w-[30%]">Total</TableCell>
              </tr>
            </thead>
            <tbody>
              <tr>
                <TableCell className="align-middle font-semibold lowercase">receita</TableCell>
                <TableCell className="align-middle font-semibold normal-case">{formatCurrency(valorRepasse)}</TableCell>
                <TableCell className="align-middle font-semibold normal-case">{formatCurrency(valorContrapartida)}</TableCell>
                <TableCell className="align-middle font-semibold normal-case">{formatCurrency(valorTotalReceitaDespesa)}</TableCell>
              </tr>
              <tr>
                <TableCell className="align-middle font-semibold lowercase">despesa</TableCell>
                <TableCell className="align-middle font-semibold normal-case">{formatCurrency(valorRepasse)}</TableCell>
                <TableCell className="align-middle font-semibold normal-case">{formatCurrency(valorContrapartida)}</TableCell>
                <TableCell className="align-middle font-semibold normal-case">{formatCurrency(valorTotalReceitaDespesa)}</TableCell>
              </tr>
            </tbody>
          </table>
        </div>
        <SectionTitle number="6" title="Plano de Aplicação dos Recursos" />
        <div className="space-y-4">
          {aplicacao.length > 0 ? aplicacao.map((grupo, index) => (
            <div key={grupo.metaId || index} className="print-no-scroll overflow-visible">
              <div className="rounded-t-lg border border-b-0 border-slate-300 bg-slate-900 px-3 py-2 text-sm font-semibold text-white">
                {grupo.metaTitulo || `Meta ${index + 1}`}
              </div>
              <table className="print-table-fit w-full border-collapse">
                <thead>
                  <tr>
                    <TableCell head className="w-[56%]">Descrição do Item / Especificação</TableCell>
                    <TableCell head align="right" className="w-[22%]">Valor Unitário</TableCell>
                    <TableCell head align="right" className="w-[22%]">Valor Total</TableCell>
                  </tr>
                </thead>
                <tbody>
                  {(grupo.items || []).map((item, itemIndex) => (
                    <tr key={item.itemId || item.id || itemIndex}>
                      <TableCell>
                        <div>{item.itemTitulo || item.titulo || item.descricao || '—'}</div>
                        {item.descricao && (item.itemTitulo || item.titulo) && (
                          <div className="text-xs text-slate-500 mt-0.5">{item.descricao}</div>
                        )}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(item.valorUnitario || 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.valorTotal || item.valor || 0)}</TableCell>
                    </tr>
                  ))}
                  <tr>
                    <TableCell align="right" colSpan={2}><strong>Subtotal</strong></TableCell>
                    <TableCell align="right"><strong>{formatCurrency(grupo.totalMeta || 0)}</strong></TableCell>
                  </tr>
                </tbody>
              </table>
              {(grupo.orcamentos || []).length > 0 && (
                <div className="border border-t-0 border-slate-300 rounded-b-lg px-3 py-2 bg-slate-50">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Orçamentos ({(grupo.orcamentos || []).length})</p>
                  <div className="flex flex-wrap gap-2">
                    {(grupo.orcamentos || []).map((orc, oIdx) => (
                      <span key={orc.id || oIdx} className="text-xs text-slate-700 border border-slate-200 rounded px-2 py-0.5 bg-white">
                        {oIdx + 1}. {orc.nomeOriginal || 'Arquivo'}
                        {orc.valor != null && orc.valor > 0 && ` — ${formatCurrency(orc.valor)}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )) : (
            <div className="rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-600">Nenhum item cadastrado para o plano de aplicação.</div>
          )}
          <div className="rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 text-right text-sm font-semibold text-slate-900">
            Total geral: {formatCurrency(subtotalAplicacao || totalPlano)}
          </div>
        </div>

        <SectionTitle number="7" title="Cronograma de Desembolso" />
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <TableCell head>Mês</TableCell>
                <TableCell head align="right">Total</TableCell>
              </tr>
            </thead>
            <tbody>
              {desembolsoMeses.map((mes, index) => (
                <tr key={`${mes.mes || mes.month || 'mes'}-${index}`}>
                  <TableCell>{normalizeMonthLabel(mes.mes || mes.month)}</TableCell>
                  <TableCell align="right">{formatCurrency(mes.valor || mes.total || mes.totalMes || 0)}</TableCell>
                </tr>
              ))}
              {desembolsoMeses.length === 0 && (
                <tr>
                  <TableCell>Nenhuma parcela cadastrada</TableCell>
                  <TableCell align="right">—</TableCell>
                </tr>
              )}
              <tr>
                <TableCell><strong>Soma Total</strong></TableCell>
                <TableCell align="right"><strong>{formatCurrency(totalDesembolsoReferencia)}</strong></TableCell>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="print-keep-together">
          <SectionTitle number="8" title="Dos Prazos" />
          <div className="space-y-3 text-sm text-slate-700">
            <p>O prazo de vigência da parceria será de <strong>{projetoPeriodo}</strong>.</p>
            <div className="rounded-xl border border-slate-200 px-4 py-3">
              <p>As contas serão prestadas conforme o cronograma de desembolso e os prazos definidos no termo da parceria.</p>
              <p className="mt-2">Prestação de contas final: até 90 dias após o término da parceria.</p>
            </div>
          </div>
        </div>

        <div className="print-keep-together">
          <SectionTitle number="9" title="Declaração" />
          <div className="rounded-2xl border border-slate-300 px-4 py-4 text-sm leading-6 text-slate-800">
            <p>
              Na qualidade de representante legal do proponente, declaro, para fins de prova junto à Prefeitura Municipal de Sinop,
              para os efeitos e sob as penas da lei, que inexiste qualquer débito em mora ou situação de inadimplência com o Tesouro
              Municipal ou qualquer órgão ou entidade da Administração Pública Municipal, direta ou indireta, que impeça a transferência
              de recursos oriundos de dotações consignadas nos orçamentos do município, na forma deste plano de trabalho.
            </p>
            <p className="mt-4">Pede deferimento.</p>

            <div className="print-keep-together mt-10 grid gap-10 md:grid-cols-2">
              <div className="border-t border-slate-400 pt-2 text-center">
                {formatText(institution?.cidade)} / {formatText(institution?.uf)} - {formatDate(new Date().toISOString())}
              </div>
              <div className="border-t border-slate-400 pt-2 text-center">
                {formatText(dirigente?.nomeCompleto)}{dirigente?.cargo ? ` - ${dirigente.cargo}` : ''}
              </div>
            </div>
          </div>
        </div>

        <div className="print-keep-together">
          <SectionTitle number="10" title="Aprovação Pela Concedente" />
          <div className="rounded-2xl border border-slate-300 px-4 py-5 text-sm text-slate-800">
            <Field label="Aprovado" value="(X) Sim    (  ) Não" />
            <div className="print-keep-with-next mt-12 border-t border-slate-400 pt-2 text-center">Concedente</div>
          </div>
        </div>
      {retificacoes.length > 0 && (
        <footer className="mt-8 border-t-2 border-amber-400 px-2 pt-4 pb-6 print-keep-together">
          <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700 mb-2">
            Histórico de Retificações
          </p>
          <div className="space-y-1">
            {retificacoes.map((r, i) => (
              <p key={r.id} className="text-[11px] text-slate-700">
                <span className="font-semibold">Retificação {retificacoes.length > 1 ? `${i + 1}` : ''}</span>
                {' '}— solicitada em{' '}
                <strong>{formatDate(r.dataHora)}</strong>
                {r.usuario ? <> por <strong>{r.usuario}</strong></> : null}.
              </p>
            ))}
          </div>
          <p className="mt-2 text-[10px] italic text-slate-500">
            Este documento foi retificado após a versão original. A versão acima é a vigente.
          </p>
        </footer>
      )}
      </main>
    </div>
  );
};

export default PlanoTrabalhoPrintPage;
